// @ts-check

import { useCallback, useMemo } from "react";
import { get } from "lodash";
import { useQuery } from "@apollo/client/react";
import { getTeamProfileImageUrl } from "src/util/group";
import URL, { url } from "src/consts/urlPaths";
import { getPinnedEntities } from "src/queries/getPinnedEntities.graphql";
import { getEntityGradient } from "src/util/gradient";
import { getTagBgImage } from "src/util/tags";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import { useOrgBranding } from "src/hooks/orgConfig/hooks/useOrgBranding";
import { useUserPinnedItems } from "./userPreferences";

/**
 * @typedef {{
 *   id: string,
 *   entityType: user_prefs.PinnedItemEntityType,
 *   name: string,
 *   avatar: string | null,
 *   gradient?: string,
 *   link: string,
 *   shape: "circle" | "square" | "tag",
 *   type?: string,
 *   onUnpin: () => void,
 *   jobTitle?: string,
 *   tagTypeName?: string,
 * }} PinnedEntity
 */

/**
 * @param {user_prefs.PinnedItem[]} items
 * @param {user_prefs.PinnedItemEntityType} entityType
 * @returns {string[]}
 */
const getIdsByEntityType = (items, entityType) => {
  return items
    .filter((item) => item.entityType === entityType)
    .map((item) => item.id);
};

/**
 * @param {user_prefs.PinnedItem} item
 * @returns {string}
 */
const getPinnedItemKey = (item) => `${item.entityType}:${String(item.id)}`;

/**
 * Custom hook to get the user's pinned items with display metadata for the side nav.
 */
export const usePinnedEntities = () => {
  const { branding } = useOrgBranding();
  const { pinnedItems, togglePinnedItem, reorderPinnedItems } =
    useUserPinnedItems();

  // Fetch teams/people for pinned entities
  const { data: pinnedEntitiesData, previousData: previousPinnedEntitiesData } =
    useQuery(getPinnedEntities, {
      fetchPolicy: "cache-and-network",
      variables: {
        teamIds: getIdsByEntityType(pinnedItems || [], "team"),
        personIds: getIdsByEntityType(pinnedItems || [], "person"),
        tagIds: getIdsByEntityType(pinnedItems || [], "tag"),
      },
      skip: !pinnedItems?.length,
    });

  const resolvedPinnedEntitiesData =
    pinnedEntitiesData ?? previousPinnedEntitiesData;

  /**
   * Make pinned entities for teams and people, preserving pinnedItems order.
   * @type {Array<PinnedEntity>}
   */
  const pinnedEntities = useMemo(() => {
    const teams = (resolvedPinnedEntitiesData?.teams || []).map(
      /** @return {[string, PinnedEntity]} */
      (team) => [
        getPinnedItemKey({ id: team.id, entityType: ENTITY_TYPES.TEAM }),
        {
          id: team.id,
          entityType: ENTITY_TYPES.TEAM,
          name: team.name,
          avatar: getTeamProfileImageUrl(get(team, "attributes")),
          gradient: getEntityGradient(ENTITY_TYPES.TEAM, branding, team),
          link: url(URL.TEAM_DETAILS_BASE_PATH, { id: team.id }),
          shape: "square",
          type: team.type,
          onUnpin: () =>
            togglePinnedItem({ id: team.id, entityType: ENTITY_TYPES.TEAM }),
        },
      ]
    );

    const people = (resolvedPinnedEntitiesData?.people || []).map(
      /** @return {[string, PinnedEntity]} */
      (person) => [
        getPinnedItemKey({
          id: person.aggregateId,
          entityType: ENTITY_TYPES.PERSON,
        }),
        {
          id: person.aggregateId,
          entityType: ENTITY_TYPES.PERSON,
          name: `${person.firstName} ${person.surname}`,
          avatar: person.avatar,
          gradient: getEntityGradient(ENTITY_TYPES.PERSON, branding, person),
          link: url(URL.PERSON_DETAILS_BASE_PATH, { id: person.aggregateId }),
          shape: "circle",
          jobTitle: person.jobTitle,
          onUnpin: () =>
            togglePinnedItem({
              id: person.aggregateId,
              entityType: ENTITY_TYPES.PERSON,
            }),
        },
      ]
    );

    const tags = (resolvedPinnedEntitiesData?.tags || []).map(
      /** @return {[string, PinnedEntity]} */
      (tag) => [
        getPinnedItemKey({ id: tag.id, entityType: ENTITY_TYPES.TAG }),
        {
          id: tag.id,
          entityType: ENTITY_TYPES.TAG,
          name: tag.displayValue,
          avatar: getTagBgImage(tag),
          gradient: getEntityGradient(ENTITY_TYPES.TAG, branding, tag),
          link: url(URL.TAG_DETAILS, { id: tag.id }),
          shape: "tag",
          tagTypeName: tag.tagTypeName,
          onUnpin: () =>
            togglePinnedItem({
              id: tag.id,
              entityType: ENTITY_TYPES.TAG,
            }),
        },
      ]
    );

    const entitiesByKey = new Map([...teams, ...people, ...tags]);

    return (pinnedItems || [])
      .map((item) => entitiesByKey.get(getPinnedItemKey(item)))
      .filter((entity) => entity != null);
  }, [resolvedPinnedEntitiesData, branding, pinnedItems, togglePinnedItem]);

  const reorderVisiblePinnedItems = useCallback(
    /**
     * @param {number} fromVisibleIndex
     * @param {number} toVisibleIndex
     * @returns {Promise<boolean>}
     */
    async (fromVisibleIndex, toVisibleIndex) => {
      if (
        fromVisibleIndex < 0 ||
        fromVisibleIndex >= pinnedEntities.length ||
        toVisibleIndex < 0 ||
        toVisibleIndex > pinnedEntities.length
      ) {
        return false;
      }

      const fromEntity = pinnedEntities[fromVisibleIndex];
      if (!fromEntity) {
        return false;
      }

      const fromPreferenceIndex = pinnedItems.findIndex(
        (item) =>
          item.entityType === fromEntity.entityType &&
          String(item.id) === String(fromEntity.id)
      );

      if (fromPreferenceIndex === -1) {
        return false;
      }

      let toPreferenceIndex;
      if (toVisibleIndex >= pinnedEntities.length) {
        const lastVisible = pinnedEntities[pinnedEntities.length - 1];
        if (!lastVisible) {
          return false;
        }

        const lastPreferenceIndex = pinnedItems.findIndex(
          (item) =>
            item.entityType === lastVisible.entityType &&
            String(item.id) === String(lastVisible.id)
        );

        if (lastPreferenceIndex === -1) {
          return false;
        }

        toPreferenceIndex = lastPreferenceIndex + 1;
      } else {
        const toEntity = pinnedEntities[toVisibleIndex];
        if (!toEntity) {
          return false;
        }

        toPreferenceIndex = pinnedItems.findIndex(
          (item) =>
            item.entityType === toEntity.entityType &&
            String(item.id) === String(toEntity.id)
        );

        if (toPreferenceIndex === -1) {
          return false;
        }
      }

      return reorderPinnedItems(fromPreferenceIndex, toPreferenceIndex);
    },
    [pinnedEntities, pinnedItems, reorderPinnedItems]
  );

  return { pinnedEntities, reorderPinnedItems, reorderVisiblePinnedItems };
};
